import { Ban } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FormFeedback } from '@/components/ui/form-feedback';
import { Input } from '@/components/ui/input';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { formatDate } from '@/lib/intl';
import { useKeyedPendingWrite } from '@/lib/use-pending-write';

type InternalSchedule =
  paths['/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/schedule']['get']['responses'][200]['content']['application/json'];
type InternalScheduleTimepoint = InternalSchedule['timepoints'][number];
type Visit =
  paths['/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/visits/{visitId}/appointment']['post']['responses'][200]['content']['application/json'];

// Resolution R11.
const TIMEPOINT_TYPE_LABEL_KEY = {
  TREATMENT_ANCHOR: 'schedule.typeAnchor',
  FOLLOW_UP: 'schedule.typeFollowUp',
} as const;
const VISIT_STATE_LABEL_KEY = {
  PLANNED: 'schedule.statePLANNED',
  SCHEDULED: 'schedule.stateSCHEDULED',
  COMPLETED: 'schedule.stateCOMPLETED',
  MISSED: 'schedule.stateMISSED',
  CANCELLED: 'schedule.stateCANCELLED',
} as const;

/**
 * The visit/appointment action table (FR-9.15-FR-9.20), rendered only once `SubjectWorkspacePanel`
 * confirms `lifecycleState === 'ENROLLED' && schedule.scheduleAvailable`. Every action row's
 * button set is derived from `row.lifecycleState` per the Domain Model's own Visit Lifecycle
 * allowed-transitions table (PLANNED/SCHEDULED -> COMPLETED/MISSED/CANCELLED are always both
 * offered; PLANNED -> SCHEDULED via agree; SCHEDULED -> SCHEDULED via move; SCHEDULED -> PLANNED
 * via cancel-appointment). Agree/move validate the chosen date against `row.windowStart`/
 * `row.windowEnd` CLIENT-SIDE before ever calling the server (Resolution R4) — the routine
 * out-of-window case never reaches the network. On success, `onVisitUpdated` lets the parent
 * patch the single affected row in place (no full schedule re-fetch needed for a visit-level
 * mutation, since target dates/windows never change from these actions).
 *
 * Post-execution hardening (2026-07-27, WCAG 2.2 SC 1.3.1/2.5.3 distinguishability finding):
 * every per-row date `<Input>` and repeated per-row action `<Button>` carries a ROW-SCOPED
 * `aria-label` — the row's own timepoint type + target date, via `rowLabelParams` below — instead
 * of the previous static, per-column-shared label every row duplicated identically (an
 * assistive-technology/voice operator could not previously tell rows apart). The appointment-date
 * input's own label key (`schedule.agreeDateForRowLabel`) is deliberately the SAME regardless of
 * Agree/Move mode — only the row identity varies, not the action — while each action Button's own
 * `aria-label` still opens with its exact visible text (SC 2.5.3 Label in Name) before the
 * row-scoped suffix.
 *
 * Post-execution hardening (2026-07-27, WCAG 2.2 SC 4.1.3 Status Messages, Finding 4): both
 * per-row error lines (`rowValidationErrors`, `rowErrors`) render through the shared `FormFeedback`
 * component (`tone="error"`, an implicit `role="alert"`/`aria-live="assertive"` region) instead of
 * a plain `<p>`, so a row-scoped write failure is announced to assistive technology the same way
 * every other new SL-0042 section's own async-set banner now is.
 */
export function ScheduleSection({
  studyId,
  subjectId,
  timepoints,
  onVisitUpdated,
}: {
  studyId: string;
  subjectId: string;
  timepoints: InternalScheduleTimepoint[];
  onVisitUpdated: (visit: Visit) => void;
}) {
  const { t } = useTranslation('clinical-study');
  // One keyed instance for all five per-Visit controls, keyed on the Visit (R4): while one
  // write on a row is in flight the other four on that same row are refused, and every other
  // row stays live.
  const { isPending, run } = useKeyedPendingWrite();
  const appointmentDateRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const performedDateRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [rowValidationErrors, setRowValidationErrors] = useState<Record<string, string>>({});

  // Resolution R11-style literal usage: `t(key, params)` where `key` is a fixed literal string —
  // `params` merely supplies the `{{type}}`/`{{targetDate}}` interpolation values.
  const rowLabelParams = (row: InternalScheduleTimepoint) => ({
    type: t(TIMEPOINT_TYPE_LABEL_KEY[row.timepointType]),
    targetDate: formatDate(row.targetDate),
  });

  const clearRowMessages = (visitId: string) => {
    setRowErrors((previous) => {
      const next = { ...previous };
      delete next[visitId];
      return next;
    });
    setRowValidationErrors((previous) => {
      const next = { ...previous };
      delete next[visitId];
      return next;
    });
  };

  const onAgreeOrMove = async (row: InternalScheduleTimepoint, method: 'agree' | 'move') => {
    clearRowMessages(row.visitId);
    const appointmentDate = appointmentDateRefs.current.get(row.visitId)?.value ?? '';
    if (appointmentDate === '') {
      setRowValidationErrors((previous) => ({
        ...previous,
        [row.visitId]: t('schedule.dateRequired'),
      }));
      return;
    }
    if (appointmentDate < row.windowStart || appointmentDate > row.windowEnd) {
      setRowValidationErrors((previous) => ({
        ...previous,
        [row.visitId]: t('schedule.outOfWindowClientError', {
          windowStart: formatDate(row.windowStart),
          windowEnd: formatDate(row.windowEnd),
        }),
      }));
      return;
    }
    const path =
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/visits/{visitId}/appointment' as const;
    const params = {
      params: { path: { studyId, subjectId, visitId: row.visitId } },
      body: { appointmentDate },
    };
    const { data, error, response } =
      method === 'agree' ? await apiClient.POST(path, params) : await apiClient.PUT(path, params);
    if (error !== undefined || !response.ok || data === undefined) {
      setRowErrors((previous) => ({
        ...previous,
        [row.visitId]: error?.detail ?? t('schedule.genericError'),
      }));
      return;
    }
    onVisitUpdated(data);
    toast.success(
      t(method === 'agree' ? 'schedule.appointmentAgreedToast' : 'schedule.appointmentMovedToast'),
    );
  };

  const onCancelAppointment = async (row: InternalScheduleTimepoint) => {
    clearRowMessages(row.visitId);
    const { data, error, response } = await apiClient.DELETE(
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/visits/{visitId}/appointment',
      { params: { path: { studyId, subjectId, visitId: row.visitId } } },
    );
    if (error !== undefined || !response.ok || data === undefined) {
      setRowErrors((previous) => ({
        ...previous,
        [row.visitId]: error?.detail ?? t('schedule.genericError'),
      }));
      return;
    }
    onVisitUpdated(data);
    toast.success(t('schedule.appointmentCancelledToast'));
  };

  const onComplete = async (row: InternalScheduleTimepoint) => {
    clearRowMessages(row.visitId);
    const performedDate = performedDateRefs.current.get(row.visitId)?.value ?? '';
    const { data, error, response } = await apiClient.POST(
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/visits/{visitId}/completion',
      { params: { path: { studyId, subjectId, visitId: row.visitId } }, body: { performedDate } },
    );
    if (error !== undefined || !response.ok || data === undefined) {
      setRowErrors((previous) => ({
        ...previous,
        [row.visitId]: error?.detail ?? t('schedule.genericError'),
      }));
      return;
    }
    onVisitUpdated(data);
    toast.success(t('schedule.completedToast'));
  };

  const onCloseOutMissed = async (row: InternalScheduleTimepoint) => {
    clearRowMessages(row.visitId);
    const { data, error, response } = await apiClient.POST(
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/visits/{visitId}/missed-close-out',
      { params: { path: { studyId, subjectId, visitId: row.visitId } } },
    );
    if (error !== undefined || !response.ok || data === undefined) {
      setRowErrors((previous) => ({
        ...previous,
        [row.visitId]: error?.detail ?? t('schedule.genericError'),
      }));
      return;
    }
    onVisitUpdated(data);
    toast.success(t('schedule.missedClosedOutToast'));
  };

  const onCancelVisit = async (row: InternalScheduleTimepoint) => {
    if (!window.confirm(t('schedule.cancelVisitConfirm'))) {
      return;
    }
    clearRowMessages(row.visitId);
    const { data, error, response } = await apiClient.POST(
      '/v1/clinical-study/studies/{studyId}/subjects/{subjectId}/visits/{visitId}/cancellation',
      { params: { path: { studyId, subjectId, visitId: row.visitId } } },
    );
    if (error !== undefined || !response.ok || data === undefined) {
      setRowErrors((previous) => ({
        ...previous,
        [row.visitId]: error?.detail ?? t('schedule.genericError'),
      }));
      return;
    }
    onVisitUpdated(data);
    toast.success(t('schedule.visitCancelledToast'));
  };

  return (
    <div className="space-y-2">
      <h3 className="card-label">{t('schedule.title')}</h3>
      <DataTable<InternalScheduleTimepoint, unknown>
        columns={[
          {
            id: 'type',
            header: t('schedule.typeLabel'),
            cell: ({ row }) => t(TIMEPOINT_TYPE_LABEL_KEY[row.original.timepointType]),
          },
          {
            accessorKey: 'targetDate',
            header: t('schedule.targetDateLabel'),
            cell: ({ getValue }) => formatDate(getValue<string>()),
          },
          {
            id: 'window',
            header: t('schedule.windowLabel'),
            cell: ({ row }) =>
              `${formatDate(row.original.windowStart)} – ${formatDate(row.original.windowEnd)}`,
          },
          {
            id: 'state',
            header: t('schedule.stateLabel'),
            cell: ({ row }) => (
              <span>
                {t(VISIT_STATE_LABEL_KEY[row.original.lifecycleState])}
                {row.original.outOfWindowIndicator === true && (
                  <span className="status-badge warning ml-2">
                    {t('schedule.outOfWindowBadge')}
                  </span>
                )}
              </span>
            ),
          },
          {
            id: 'appointment',
            header: t('schedule.appointmentLabel'),
            cell: ({ row }) =>
              row.original.appointmentDate !== null
                ? formatDate(row.original.appointmentDate)
                : t('schedule.noAppointment'),
          },
          {
            id: 'actions',
            header: '',
            cell: ({ row }) => {
              const visitRow = row.original;
              const isActive =
                visitRow.lifecycleState === 'PLANNED' || visitRow.lifecycleState === 'SCHEDULED';
              if (!isActive) {
                return null;
              }
              const labelParams = rowLabelParams(visitRow);
              return (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="date"
                      className="w-40"
                      aria-label={t('schedule.agreeDateForRowLabel', labelParams)}
                      ref={(element) => {
                        if (element) appointmentDateRefs.current.set(visitRow.visitId, element);
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={t(
                        visitRow.lifecycleState === 'PLANNED'
                          ? 'schedule.agreeActionForRowLabel'
                          : 'schedule.moveActionForRowLabel',
                        labelParams,
                      )}
                      disabled={isPending(visitRow.visitId)}
                      onClick={() =>
                        void run(visitRow.visitId, () =>
                          onAgreeOrMove(
                            visitRow,
                            visitRow.lifecycleState === 'PLANNED' ? 'agree' : 'move',
                          ),
                        )
                      }
                    >
                      {visitRow.lifecycleState === 'PLANNED'
                        ? t('schedule.agreeAction')
                        : t('schedule.moveAction')}
                    </Button>
                    {visitRow.lifecycleState === 'SCHEDULED' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label={t('schedule.cancelAppointmentActionForRowLabel', labelParams)}
                        disabled={isPending(visitRow.visitId)}
                        onClick={() =>
                          void run(visitRow.visitId, () => onCancelAppointment(visitRow))
                        }
                      >
                        {t('schedule.cancelAppointmentAction')}
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="date"
                      className="w-40"
                      aria-label={t('schedule.performedDateForRowLabel', labelParams)}
                      ref={(element) => {
                        if (element) performedDateRefs.current.set(visitRow.visitId, element);
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={t('schedule.completeActionForRowLabel', labelParams)}
                      disabled={isPending(visitRow.visitId)}
                      onClick={() => void run(visitRow.visitId, () => onComplete(visitRow))}
                    >
                      {t('schedule.completeAction')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={t('schedule.missedCloseOutActionForRowLabel', labelParams)}
                      disabled={isPending(visitRow.visitId)}
                      onClick={() => void run(visitRow.visitId, () => onCloseOutMissed(visitRow))}
                    >
                      {t('schedule.missedCloseOutAction')}
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      aria-label={t('schedule.cancelVisitActionForRowLabel', labelParams)}
                      disabled={isPending(visitRow.visitId)}
                      onClick={() => void run(visitRow.visitId, () => onCancelVisit(visitRow))}
                    >
                      <Ban className="size-4" aria-hidden="true" />
                      {t('schedule.cancelVisitAction')}
                    </Button>
                  </div>
                  {rowValidationErrors[visitRow.visitId] && (
                    <FormFeedback tone="error">
                      {rowValidationErrors[visitRow.visitId]}
                    </FormFeedback>
                  )}
                  {rowErrors[visitRow.visitId] && (
                    <FormFeedback tone="error">{rowErrors[visitRow.visitId]}</FormFeedback>
                  )}
                </div>
              );
            },
          },
        ]}
        data={timepoints}
        emptyMessage={t('schedule.notAvailableMessage')}
      />
    </div>
  );
}
